import type { SettingDefinition, SettingsContribution } from '@repo/commons/settings'
import type { DetailedSelectItem } from '@repo/commons/models'
import { untrack } from 'svelte'

export interface DraftDeps {
	settings: ReturnType<typeof import('@repo/commons/settings').useSettings>
	contributionsData: () => SettingsContribution[]
	searchResults: () => (SettingDefinition & { namespace: string; displayName: string })[]
	presetsData: () => { id: string; name: string; description?: string }[]
	activeTab: () => string
}

interface CategoryNode {
	name: string
	children: CategoryNode[]
	definitions: SettingDefinition[]
}

function buildCategoryTree(defs: SettingDefinition[]): CategoryNode[] {
	const root: CategoryNode = { name: '', children: [], definitions: [] }

	for (const def of defs) {
		let current = root
		for (const segment of def.category) {
			let child = current.children.find((c) => c.name === segment)
			if (!child) {
				child = { name: segment, children: [], definitions: [] }
				current.children.push(child)
			}
			current = child
		}
		current.definitions.push(def)
	}

	return root.children
}

export const CUSTOM_PRESET_ID = '__custom__'

export function useSettingsDraft(deps: DraftDeps) {
	const { settings } = deps

	let draftValues = $state<Record<string, unknown>>({})
	let draftPresetId = $state<string | null>(settings.activePreset)

	function buildDraftFromUserValues() {
		const data = deps.contributionsData()
		if (!data.length) return
		const draft: Record<string, unknown> = {}
		for (const c of data) {
			for (const def of c.definitions) {
				draft[def.key] = settings.get(def.key)
			}
		}
		draftValues = draft
	}

	async function resetDraftFromStorage() {
		const data = deps.contributionsData()
		if (!data.length) return

		const presetId = settings.activePreset
		if (presetId) {
			try {
				const res = await fetch(`/api/shared/settings/presets/${presetId}`)
				if (res.ok) {
					const preset = (await res.json()) as { values: Record<string, unknown> }
					const allDefs = data.flatMap((c) => c.definitions)
					const userVals = settings.userValues
					const draft: Record<string, unknown> = {}
					for (const def of allDefs) {
						draft[def.key] = userVals[def.key] ?? preset.values[def.key] ?? def.defaultValue
					}
					draftValues = draft
					return
				}
			} catch {
				// Fall through to local values
			}
		}

		buildDraftFromUserValues()
	}

	function resetOnOpen(visible: boolean) {
		if (!visible) return
		untrack(() => {
			draftPresetId = settings.activePreset
			resetDraftFromStorage()
		})
	}

	function getDraftValue(key: string): unknown {
		return draftValues[key]
	}

	const getAllDefinitions = (): SettingDefinition[] =>
		deps.contributionsData().flatMap(({ definitions }) => definitions)

	const getEnabledKeys = (key: string): readonly string[] =>
		getAllDefinitions().find((def) => def.key === key)?.enables ?? []

	const isDraftValueLocked = (key: string): boolean =>
		getAllDefinitions().some(
			({ key: lockingKey, enables }) =>
				draftValues[lockingKey] === true && (enables?.includes(key) ?? false)
		)

	function setDraftValue(key: string, value: unknown): void {
		const enabledKeys: readonly string[] = value === true ? getEnabledKeys(key) : []
		const enabledValues: Record<string, boolean> = Object.fromEntries(
			enabledKeys.map((enabledKey) => [enabledKey, true])
		)

		draftValues = { ...draftValues, ...enabledValues, [key]: value }
		draftPresetId = null
	}

	function replaceDraftValues(newDraft: Record<string, unknown>): void {
		draftValues = newDraft
		draftPresetId = null
	}

	const tabs = $derived.by(() => {
		const contributions = deps.contributionsData()
		const tabSet = new Set<string>()
		for (const c of contributions) {
			for (const def of c.definitions) {
				tabSet.add(def.tab ?? 'ViewSettings.tabs.general')
			}
		}
		return Array.from(tabSet).map((name) => ({
			id: name,
			name,
			isActive: deps.activeTab() === name
		}))
	})

	const groupedByApp = $derived.by(() => {
		const searchResults = deps.searchResults()
		const contributions = deps.contributionsData()
		const byNamespace = new Map<string, { displayName: string; definitions: SettingDefinition[] }>()

		for (const def of searchResults) {
			const existing = byNamespace.get(def.namespace)
			if (existing) {
				existing.definitions.push(def)
			} else {
				byNamespace.set(def.namespace, {
					displayName: def.displayName,
					definitions: [def]
				})
			}
		}

		const result: {
			contribution: { namespace: string; displayName: string; toggleAllCategories?: string[] }
			tree: CategoryNode[]
		}[] = []

		for (const [namespace, { displayName, definitions }] of byNamespace) {
			const sorted = definitions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
			const contrib = contributions.find((c) => c.namespace === namespace)
			result.push({
				contribution: { namespace, displayName, toggleAllCategories: contrib?.toggleAllCategories },
				tree: buildCategoryTree(sorted)
			})
		}

		return result
	})

	function handleSave(onClose: () => void) {
		settings.resetAll()

		const allDefs = deps.contributionsData().flatMap((c) => c.definitions)
		for (const def of allDefs) {
			const draftVal = draftValues[def.key]
			const defaultVal = def.defaultValue
			if (draftVal !== defaultVal) {
				settings.set(def.key, draftVal)
			}
		}

		if (draftPresetId) {
			settings.setActivePresetId(draftPresetId)
		} else {
			settings.clearPreset()
		}

		onClose()
	}

	const presetItems = $derived.by((): DetailedSelectItem[] => {
		const presets = deps.presetsData()
		const items: DetailedSelectItem[] = presets.map((p) => ({
			id: p.id,
			title: p.name
		}))
		items.push({ id: CUSTOM_PRESET_ID, title: 'ViewSettings.customPreset' })
		return items
	})

	const selectedPresetItem = $derived.by((): DetailedSelectItem | null => {
		const id = draftPresetId ?? CUSTOM_PRESET_ID
		return presetItems.find((item) => item.id === id) ?? null
	})

	let isLoadingPreset = $state(false)

	async function handlePresetSelect(item: DetailedSelectItem | null) {
		if (!item || item.id === CUSTOM_PRESET_ID) {
			draftPresetId = null
			return
		}

		isLoadingPreset = true
		try {
			const res = await fetch(`/api/shared/settings/presets/${item.id}`)
			if (!res.ok) throw new Error('Failed to fetch preset')
			const preset = (await res.json()) as { id: string; values: Record<string, unknown> }

			draftPresetId = preset.id

			const allDefs = deps.contributionsData().flatMap((c) => c.definitions)
			const draft: Record<string, unknown> = {}
			for (const def of allDefs) {
				draft[def.key] = preset.values[def.key] ?? def.defaultValue
			}
			draftValues = draft
		} catch (err) {
			console.error('[ViewSettings] Failed to apply preset:', err)
		} finally {
			isLoadingPreset = false
		}
	}

	return {
		get tabs() {
			return tabs
		},
		get groupedByApp() {
			return groupedByApp
		},
		get presetItems() {
			return presetItems
		},
		get selectedPresetItem() {
			return selectedPresetItem
		},
		get isLoadingPreset() {
			return isLoadingPreset
		},
		resetOnOpen,
		getDraftValue,
		setDraftValue,
		isDraftValueLocked,
		replaceDraftValues,
		handleSave,
		handlePresetSelect
	}
}
